/**
 * Iter 187 — Cart Recovery settings page.
 * ============================================================================
 *
 * Vendor UI for the abandoned-cart recovery flow:
 *   • Toggle to enable/disable the automation
 *   • Configure reminder timings (1h / 24h / 48h defaults)
 *   • Optional discount code + subject line
 *   • Recent abandoned carts table
 *   • "Test fire" button to run the cron worker on demand
 *
 * Backend endpoints (see marketplace/shipping_routes.py):
 *   GET/PUT /api/builder/cart-recovery/settings
 *   GET     /api/builder/cart-recovery/list
 *   POST    /api/builder/cart-recovery/test-fire
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingBag, Save, Zap, Clock, Tag, Mail, TrendingUp } from 'lucide-react';
import { GlassCard, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SettingsCartRecovery() {
  const [settings, setSettings] = useState(null);
  const [carts, setCarts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [firing, setFiring] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/builder/cart-recovery/settings`, auth).then((r) => r.data).catch(() => null),
      axios.get(`${API}/builder/cart-recovery/list`, auth).then((r) => r.data).catch(() => []),
    ]).then(([s, c]) => {
      setSettings(s);
      setCarts(c || []);
    });
  }, []);

  const patch = (delta) => setSettings((s) => ({ ...s, ...delta }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/builder/cart-recovery/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cart recovery settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const testFire = async () => {
    setFiring(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/builder/cart-recovery/test-fire`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Batch complete: ${data.sent} email(s) sent, ${data.processed} cart(s) checked`);
      const { data: list } = await axios.get(`${API}/builder/cart-recovery/list`, { headers: { Authorization: `Bearer ${token}` } });
      setCarts(list || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test-fire failed');
    } finally { setFiring(false); }
  };

  if (!settings) return <div className="p-8 text-white/60" data-testid="cart-recovery-loading">Loading…</div>;

  return (
    <div className="relative" data-testid="settings-cart-recovery">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-4xl">
        <Link to="/website-builder/settings" data-testid="cart-recovery-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] font-semibold">
          <ArrowLeft size={12} /> Go Back
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Cart Recovery</h1>
            <p className="text-[#A5A8C9] text-sm mt-1.5 max-w-xl">
              Automatically email shoppers who leave items in their cart. Industry-standard recovery rates are 10-15% — that's real money you're leaving on the table without this.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cart-recovery-save"
          >
            <Save size={13} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Enable toggle */}
        <GlassCard glow className="p-6">
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="mt-1 w-5 h-5 accent-[#A78BFA]"
              data-testid="cart-recovery-enabled"
            />
            <div className="flex-1">
              <p className="text-white font-bold text-base flex items-center gap-2">
                <Zap size={15} className="text-[#A78BFA]" /> Enable automated recovery emails
              </p>
              <p className="text-xs text-white/60 mt-1">
                When on, the cron worker sends up to 3 reminder emails per abandoned cart. When off, no emails go out — but carts are still captured so you can enable later without losing data.
              </p>
            </div>
          </label>
        </GlassCard>

        {/* Timings */}
        <GlassCard className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-3 flex items-center gap-1.5"><Clock size={11} /> Reminder timings</p>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((n) => (
              <label key={n} className="block">
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Reminder #{n} — after (hours)</p>
                <input
                  type="number" min="0" max="720"
                  value={settings[`reminder_${n}_after_hours`]}
                  onChange={(e) => patch({ [`reminder_${n}_after_hours`]: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
                  data-testid={`cart-recovery-reminder-${n}`}
                />
              </label>
            ))}
          </div>
          <p className="text-[10px] text-white/40 mt-2">Recommended: 1h → 24h → 48h. Set a value to 0 to disable that reminder.</p>
        </GlassCard>

        {/* Discount + subject */}
        <GlassCard className="p-6 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-1.5 flex items-center gap-1.5"><Tag size={11} /> Discount code (optional)</p>
            <input
              value={settings.discount_code || ''}
              onChange={(e) => patch({ discount_code: e.target.value.toUpperCase().slice(0, 24) })}
              placeholder="COMEBACK10"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-mono uppercase tracking-wider focus:border-[#A78BFA]/50 outline-none"
              data-testid="cart-recovery-discount-code"
            />
            <p className="text-[10px] text-white/40 mt-1">Shown in the email if set. Create the matching discount in your Discounts dashboard first.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-1.5 flex items-center gap-1.5"><Mail size={11} /> Subject line</p>
            <input
              value={settings.subject_line || ''}
              onChange={(e) => patch({ subject_line: e.target.value.slice(0, 80) })}
              placeholder="You left something behind 💜"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
              data-testid="cart-recovery-subject"
            />
          </div>
        </GlassCard>

        {/* Recent captures */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] flex items-center gap-1.5"><TrendingUp size={11} /> Recent abandoned carts ({carts.length})</p>
              <p className="text-xs text-white/50 mt-1">Captured when a shopper enters their email at checkout but doesn't complete.</p>
            </div>
            <button
              onClick={testFire}
              disabled={firing}
              className="px-3 py-2 rounded-lg bg-[#A78BFA]/15 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold uppercase tracking-wider hover:bg-[#A78BFA]/25 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="cart-recovery-test-fire"
              title="Run the cron worker once and dispatch any due reminder emails"
            >
              <Zap size={12} /> {firing ? 'Firing…' : 'Test Fire'}
            </button>
          </div>

          {carts.length === 0 ? (
            <GlassCard className="p-8 text-center">
              <ShoppingBag size={28} className="text-[#A78BFA] mx-auto mb-2" />
              <p className="text-sm text-white/70">No abandoned carts captured yet.</p>
              <p className="text-xs text-white/40 mt-1">Once shoppers start entering their email at checkout, they'll appear here.</p>
            </GlassCard>
          ) : (
            <GlassCard className="p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] font-bold text-white/50">
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Items</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Age</th>
                    <th className="px-4 py-2 text-right">Reminders</th>
                  </tr>
                </thead>
                <tbody>
                  {carts.map((c) => {
                    const ageMs = Date.now() - new Date(c.created_at).getTime();
                    const ageH = Math.max(0, Math.floor(ageMs / 3600000));
                    const sent = [c.reminder_1_sent_at, c.reminder_2_sent_at, c.reminder_3_sent_at].filter(Boolean).length;
                    return (
                      <tr key={c.id} className="border-t border-white/[0.05]" data-testid={`abandoned-cart-${c.id}`}>
                        <td className="px-4 py-2.5 text-white">{c.customer_email}</td>
                        <td className="px-4 py-2.5 text-white/60">{(c.items || []).length} item(s)</td>
                        <td className="px-4 py-2.5 text-right text-white">${Number(c.total || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right text-white/60">{ageH < 1 ? '<1h' : `${ageH}h`}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="text-[10px] font-bold text-[#A78BFA]">{sent}/3 sent</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
}
